import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import FAQ from '@/components/base/FAQ';
import { articles, categories, faqItems } from '@/mocks/data';
import { WebSiteSchema, FAQPageSchema, WebPageSchema } from '@/components/base/SchemaOrg';

export default function HomePage() {
  const featuredArticles = articles.slice(0, 6);
  const latestArticles = articles.slice(6, 12);

  return (
    <>
      <ScrollToTop />
      <WebSiteSchema />
      <FAQPageSchema items={faqItems} />
      <WebPageSchema
        title="母子关系心理健康指南 - 专业心理分析与干预方案"
        description="从专业心理学角度分析母子关系中的边界模糊、情感依赖与依恋障碍问题，提供科学的心理干预与改善建议。"
        url="https://www.maternal-psych-guide.cn"
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20artistic%20composition%20with%20soft%20flowing%20curves%2C%20gradient%20from%20warm%20terracotta%20to%20cream%2C%20gentle%20ambient%20light%2C%20minimalist%20elegant%20design%2C%20healing%20psychology%20concept%2C%20emotional%20wellness%20visual%2C%20no%20text%2C%20no%20people%2C%20calm%20soothing%20atmosphere&width=1920&height=1080&seq=home-hero-2026&orientation=landscape"
              alt="母子关系心理健康"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/35"></div>
          <div className="relative w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <span className="inline-block px-4 py-1.5 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 text-xs font-medium mb-6 border border-background-50/20">
              专业心理学内容平台
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
              理解母子关系<br />守护心理健康
            </h1>
            <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-10 leading-relaxed">
              从专业心理学角度分析母子关系中的边界模糊、情感依赖与依恋障碍问题，帮助家庭识别异常亲密行为的成因，并提供科学的心理干预与改善建议。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                浏览全部文章
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 font-medium text-sm whitespace-nowrap hover:bg-background-50/25 transition-colors cursor-pointer border border-background-50/20"
              >
                了解我们
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                探索核心主题
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                六大专业分类，帮助你全面理解母子关系中的各种心理议题
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category?tab=${cat.id}`}
                  className="group flex flex-col items-center text-center p-5 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 mb-3 group-hover:bg-accent-200 transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-foreground-400 line-clamp-2">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  精选文章
                </h2>
                <p className="text-sm text-foreground-500">由持证心理咨询师精心撰写的专业内容</p>
              </div>
              <Link
                to="/category"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link
                to="/category"
                className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium cursor-pointer"
              >
                查看全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  最新发布
                </h2>
                <p className="text-sm text-foreground-500">持续更新，关注母子关系心理健康前沿</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-500">
                关于母子关系心理健康的常见疑问解答
              </p>
            </div>
            <FAQ items={faqItems} />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 bg-background-800 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20flowing%20shapes%20in%20dark%20terracotta%20and%20deep%20brown%20tones%2C%20minimalist%20elegant%20design%2C%20soft%20gradient%20texture%2C%20calm%20professional%20atmosphere%2C%20no%20text%2C%20healing%20concept%20background&width=1920&height=600&seq=cta-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top opacity-20"
            />
          </div>
          <div className="relative w-full max-w-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
              准备好改善你的亲子关系了吗？
            </h2>
            <p className="text-sm md:text-base text-background-300 mb-8 leading-relaxed">
              每个家庭都值得拥有健康和谐的亲子关系。开始阅读我们的专业文章，迈出改变的第一步。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full bg-background-50/10 backdrop-blur-sm text-background-50 font-medium text-sm whitespace-nowrap hover:bg-background-50/20 transition-colors cursor-pointer border border-background-50/20"
              >
                联系我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}